import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  extractCoordsFromLogLine,
  TRL_DIFFICULTIES,
  TRL_DIFFICULTY_EMOJI,
  TRL_DIFFICULTY_LABELS,
  type PlatformGalaxyFaction,
} from '@swtorbit/shared-socket-events';
import { TagInput, type TagInputHandle } from '../shared/TagInput';
import { usePlatformAreas, type PlatformAreaEntry } from '../../../hooks/usePlatformAreas';
import { usePlatformLocate } from '../../../hooks/usePlatformLocate';
import { PlatformLocationPanels } from './PlatformLocationPanels';

export interface MetadataFormValues {
  name?: string;
  difficulty: string;
  faction: string;
  tags: string[];
  /** Platform area (combat-log area id). */
  logAreaId: number;
  x: number;
  y: number;
  z: number;
  facing?: number;
}

export interface MetadataFormInitial {
  name?: string;
  difficulty?: string | null;
  faction?: string;
  tags?: string[];
  logAreaId?: number;
  x?: number;
  y?: number;
  z?: number;
  facing?: number;
}

interface MetadataFormProps {
  mode: 'add' | 'edit';
  idPrefix: string;
  initial?: MetadataFormInitial;
  submitLabel: string;
  submitBusyLabel: string;
  onSubmit: (values: MetadataFormValues) => Promise<void>;
  onCancel?: () => void;
}

/** The rating buttons, in order — glyph then name, as they read in the picker. */
const DIFFICULTIES = TRL_DIFFICULTIES.map((value) => ({
  value,
  label: `${TRL_DIFFICULTY_EMOJI[value]} ${TRL_DIFFICULTY_LABELS[value]}`,
}));

const LAST_AREA_KEY = 'swtorbit.trl.lastLogAreaId';

const FACTIONS = [
  { value: 'both',     label: '⚪ Both' },
  { value: 'imperial', label: '🔴 Imperial' },
  { value: 'republic', label: '🔵 Republic' },
] as const;

// Derive the faction an area requires. A Republic- or Imperial-only area forces
// its faction; an area open to both defaults to 'both' (any choice allowed). No
// area (nothing selected) leaves it untouched.
function deriveFaction(areaFaction: PlatformGalaxyFaction | null | undefined, current: string): string {
  if (areaFaction === 'republic') return 'republic';
  if (areaFaction === 'imperial') return 'imperial';
  if (areaFaction === 'both')     return 'both';
  return current;
}

/** Option text: the area name, plus its faction when it is faction-only (Balmorra). */
function areaOptionLabel(area: PlatformAreaEntry): string {
  if (area.galaxyFaction === 'imperial') return `${area.name} (Imperial)`;
  if (area.galaxyFaction === 'republic') return `${area.name} (Republic)`;
  return area.name;
}

/** A finite number from a form field, or null. */
function fieldNumber(value: string): number | null {
  if (value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function MetadataForm({
  mode,
  idPrefix,
  initial,
  submitLabel,
  submitBusyLabel,
  onSubmit,
  onCancel,
}: MetadataFormProps) {
  const { planets, byId, loading: areasLoading, error: areasError } = usePlatformAreas();
  const [name, setName] = useState(initial?.name ?? '');
  const [difficulty, setDifficulty] = useState(initial?.difficulty ?? '');
  const [tags, setTags] = useState<string[]>(initial?.tags ?? []);
  const initialAreaId =
    initial?.logAreaId != null
      ? String(initial.logAreaId)
      : mode === 'add' ? (localStorage.getItem(LAST_AREA_KEY) ?? '') : '';
  const [logAreaId, setLogAreaId] = useState(initialAreaId);
  const [faction, setFaction] = useState(initial?.faction ?? '');
  const [x, setX] = useState(initial?.x === undefined ? '' : String(initial.x));
  const [y, setY] = useState(initial?.y === undefined ? '' : String(initial.y));
  const [z, setZ] = useState(initial?.z === undefined ? '' : String(initial.z));
  const [facing, setFacing] = useState(initial?.facing === undefined ? '' : String(initial.facing));
  const [logLine, setLogLine] = useState('');
  const [logExpanded, setLogExpanded] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tagInputRef = useRef<TagInputHandle>(null);

  const selectedArea = useMemo(
    () => (logAreaId ? byId.get(Number(logAreaId)) ?? null : null),
    [byId, logAreaId],
  );

  // One-shot, once areas load: a remembered add-mode area that no longer
  // exists is cleared.
  const didInitArea = useRef(false);
  useEffect(() => {
    if (didInitArea.current || byId.size === 0) return;
    didInitArea.current = true;
    if (logAreaId && !byId.has(Number(logAreaId))) setLogAreaId('');
  }, [byId, logAreaId]);

  // One-shot: derive the faction from a pre-selected area once areas load (add
  // mode with a remembered area and no explicit faction). Never clobbers an
  // explicit / edit-mode faction.
  const didInitFaction = useRef(false);
  useEffect(() => {
    if (didInitFaction.current) return;
    if (initial?.faction) { didInitFaction.current = true; return; }
    if (selectedArea) {
      didInitFaction.current = true;
      setFaction((prev) => deriveFaction(selectedArea.galaxyFaction, prev));
    }
  }, [selectedArea, initial?.faction]);

  const areaFaction = selectedArea?.galaxyFaction;

  // Live preview: names, faction and both maps with a pin, as the area and
  // coordinates are picked, typed or pasted.
  const locate = usePlatformLocate(
    selectedArea ? selectedArea.logAreaId : null,
    fieldNumber(x),
    fieldNumber(y),
    fieldNumber(z),
  );

  const handleLogLine = useCallback((value: string) => {
    setLogLine(value);
    const parsed = extractCoordsFromLogLine(value);
    if (parsed) {
      setX(String(parsed.x));
      setY(String(parsed.y));
      setZ(String(parsed.z));
      setFacing(String(parsed.facing));
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    const pendingTag = tagInputRef.current?.flush() ?? null;
    const finalTags = pendingTag ? [...tags, pendingTag] : tags;
    setError(null);
    if (!selectedArea) {
      setError('Area is required.');
      return;
    }
    if (x === '' || y === '' || z === '') {
      setError('X, Y and Z coordinates are required.');
      return;
    }
    if (!difficulty) {
      setError('Please select a difficulty.');
      return;
    }
    if (!faction) {
      setError('Please select a faction.');
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit({
        name: name.trim() || undefined,
        difficulty,
        faction,
        tags: finalTags,
        logAreaId: selectedArea.logAreaId,
        x: Number(x),
        y: Number(y),
        z: Number(z),
        facing: facing === '' ? undefined : Number(facing),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  }, [name, difficulty, faction, tags, selectedArea, x, y, z, facing, onSubmit]);

  return (
    <div className="d-flex flex-column gap-xs">

      <p className="fs-xs text-slate m-0 text-uppercase font-semibold">Geometry</p>

      <div className="d-flex gap-xs" style={{ alignItems: 'flex-end' }}>

        <div className="field flex-1 m-0">
          <label htmlFor={`${idPrefix}-area`}>Planet / Area</label>
          <select
            id={`${idPrefix}-area`}
            className="input"
            value={logAreaId}
            disabled={areasLoading && planets.length === 0}
            onChange={(e) => {
              const v = e.target.value;
              setLogAreaId(v);
              if (mode === 'add' && v) localStorage.setItem(LAST_AREA_KEY, v);
              const area = v ? byId.get(Number(v)) : undefined;
              setFaction((prev) => deriveFaction(area?.galaxyFaction, prev));
            }}
          >
            <option value="">{areasLoading && planets.length === 0 ? 'Loading areas…' : '— select —'}</option>
            {planets.map((p) => (
              <optgroup key={p.planetName} label={p.planetName}>
                {p.areas.map((a) => {
                  const entry = byId.get(a.logAreaId);
                  return (
                    <option key={a.logAreaId} value={a.logAreaId}>
                      {entry ? areaOptionLabel(entry) : a.name}
                    </option>
                  );
                })}
              </optgroup>
            ))}
          </select>
        </div>

        <div className="field m-0" style={{ flexShrink: 0 }}>
          <label>Faction</label>
          <div className="d-flex gap-2xs">
            {FACTIONS.map((f) => {
              const disabled = areaFaction === 'republic'
                ? f.value !== 'republic'
                : areaFaction === 'imperial'
                ? f.value !== 'imperial'
                : false;
              return (
                <button
                  key={f.value}
                  type="button"
                  className={`btn btn--sm ${faction === f.value ? '' : 'btn--outline btn--slate'}`}
                  onClick={() => setFaction(f.value)}
                  disabled={disabled}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

      </div>

      {areasError && <p className="text-red fs-sm m-0">{areasError}</p>}

      <div className="d-flex gap-xs">
        <div className="field flex-1">
          <label htmlFor={`${idPrefix}-x`}>X</label>
          <input id={`${idPrefix}-x`} type="number" className="input" value={x} onChange={(e) => setX(e.target.value)} />
        </div>
        <div className="field flex-1">
          <label htmlFor={`${idPrefix}-y`}>Y</label>
          <input id={`${idPrefix}-y`} type="number" className="input" value={y} onChange={(e) => setY(e.target.value)} />
        </div>
        <div className="field flex-1">
          <label htmlFor={`${idPrefix}-z`}>Z</label>
          <input id={`${idPrefix}-z`} type="number" className="input" value={z} onChange={(e) => setZ(e.target.value)} />
        </div>
      </div>


      <div className="field">
        <label htmlFor={`${idPrefix}-facing`}>
          Facing <span className="text-slate fs-xs">(optional)</span>
        </label>
        <input
          id={`${idPrefix}-facing`}
          type="number"
          className="input"
          value={facing}
          onChange={(e) => setFacing(e.target.value)}
        />
      </div>

      <div className="d-flex gap-xs">
        <button
          type="button"
          className="btn btn--outline btn--slate btn--sm"
          style={{ flexShrink: 0, alignSelf: 'flex-start' }}
          onClick={() => setLogExpanded((v) => !v)}
          aria-expanded={logExpanded}
          aria-label="Toggle combat log input"
        >
          {logExpanded ? '▾' : '▸'}
        </button>
        <div className="d-flex flex-column gap-xs flex-1">
          <span
            className="fs-sm font-semibold"
            style={{ cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setLogExpanded((v) => !v)}
          >
            Fill from combat log
          </span>
          {logExpanded && (
            <>
              <textarea
                id={`${idPrefix}-logline`}
                className="input"
                rows={2}
                placeholder="e.g. [@Character#123456789|(-148.89,-46.09,-0.41,-67.46)|(3244/3244)]"
                value={logLine}
                onChange={(e) => handleLogLine(e.target.value)}
              />
              <p className="field__hint fs-xs m-0">
                Paste a Seismic Excavator/Scanner log line to auto-fill X/Y/Z/Facing.
              </p>
            </>
          )}
        </div>
      </div>

      <PlatformLocationPanels {...locate} />

      <hr className="m-0" style={{ borderColor: 'var(--color-teal-dark)', opacity: 0.5 }} />

      <p className="fs-xs text-slate m-0 text-uppercase font-semibold">Metadata</p>

      <div className="field">
        <label>Difficulty</label>
        <div className="d-flex gap-2xs">
          {DIFFICULTIES.map((d) => (
            <button
              key={d.value}
              type="button"
              className={`btn btn--sm flex-1 ${difficulty === d.value ? '' : 'btn--outline btn--slate'}`}
              onClick={() => setDifficulty(d.value)}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-space rounded-sm d-flex flex-column gap-xs" style={{ padding: '12px 14px' }}>
        <div>
          <p className="fs-xs text-uppercase font-semibold m-0" style={{ letterSpacing: '0.05em' }}>
            Internal Use Only
          </p>
          <p className="fs-xs text-slate m-0" style={{ marginTop: '2px' }}>
            Name and tags are for administrator organisation and are never displayed publicly.
          </p>
        </div>
        <div className="field m-0">
          <label htmlFor={`${idPrefix}-name`}>
            Name <span className="text-slate fs-xs">(optional)</span>
          </label>
          <input
            id={`${idPrefix}-name`}
            type="text"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <TagInput ref={tagInputRef} id={`${idPrefix}-tags`} label="Tags (optional)" tags={tags} onChange={setTags} />
      </div>

      <hr className="m-0" style={{ borderColor: 'var(--color-teal-dark)', opacity: 0.5 }} />

      {error && <p className="text-red fs-sm m-0">{error}</p>}

      <div className="d-flex flex-column gap-xs align-items-center">
        <button
          type="button"
          className={`text-uppercase fw-bold w-100 mw-12 ${mode === 'add' ? 'btn btn--fancy btn--green' : 'btn btn--green'}`}
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? submitBusyLabel : submitLabel}
        </button>
        {onCancel && (
          <button type="button" className="btn btn--outline btn--slate" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
